import { 
  doc, 
  updateDoc, 
  arrayUnion, 
  collection, 
  addDoc,
  query,
  orderBy,
  limit,
  getDocs,
  getDoc,
  setDoc,
  Timestamp 
} from 'firebase/firestore';
import { db } from './config';
import { ClimbingRoute } from '../../models/climbing/ClimbingRoute';
import { Climber } from '../../models/user/Climber';
import { getClimber, updateClimberFields } from './climber';
import { ClimbPerformance } from '../../models/climbing/Grade';

// Interface for climb record stored in user document and archive
interface ClimbRecord {
  routeId: string;
  gymId: string;
  date: number; // Timestamp
  performance: ClimbPerformance;
  grade?: number;
  style?: string;
  notes?: string;
  hangs?: number;
  attempt?: number;
  reachedTop?: boolean;
  
  // Additional route details copied to preserve history
  color?: string;
  rope?: string;
  setterId?: string;
  setterName?: string;
  routeNote?: string;
  routeComments?: string;
  routeDate?: number;
  status?: string;
  picture?: string;
  location?: string;
  points?: number;
}

// Maximum number of climbs to store directly in user document
const MAX_RECENT_CLIMBS = 1000;

/**
 * Updates the gym ranking document with a new climb record
 * Prevents duplicate entries for the same user and route
 */
const updateGymRanking = async (
  gymId: string,
  userId: string,
  userName: string,
  routeId: string,
  points?: number
): Promise<void> => {
  try {
    // Reference to the ranking document under the gym
    const rankingRef = doc(db, 'gyms', gymId, 'rankings', 'current');
    console.log('Updating ranking at path:', `gyms/${gymId}/rankings/current`);
    
    // Get the current ranking document
    const rankingDoc = await getDoc(rankingRef);
    console.log('Current ranking document exists:', rankingDoc.exists());
    
    // Get current rankings or initialize if it doesn't exist
    let rankings: any[] = rankingDoc.exists() ? rankingDoc.data().rankings || [] : [];
    console.log('Current rankings length:', rankings.length);
    
    // Check if this user already has this route in the rankings
    const existingEntry = rankings.find(entry => 
      entry.climberId === userId && entry.routeId === routeId
    );
    
    if (existingEntry) {
      console.log(`User ${userId} already has route ${routeId} in rankings. Skipping.`);
      return; // Exit without adding a duplicate entry
    }
    
    // Create the entry object with route ID as key
    const entry = {
      routeId,
      climberId: userId,
      climberName: userName,
      timestamp: Date.now()
    };
    console.log('Adding ranking entry:', entry);
    
    // Add new entry to rankings array
    rankings.push(entry);
    
    // Save the updated rankings
    await setDoc(rankingRef, { rankings }, { merge: true });
    console.log(`Updated gym ranking for gym ${gymId} with climber ${userId}. New length:`, rankings.length);
  } catch (error) {
    console.error('Error updating gym ranking:', error);
    // Don't throw the error to prevent the main climb recording from failing
  }
};

/**
 * Fetches the gym ranking data
 */
export const getGymRanking = async (gymId: string): Promise<any[]> => {
  try {
    // Reference to the ranking document under the gym
    const rankingRef = doc(db, 'gyms', gymId, 'rankings', 'current');
    console.log('Fetching rankings from path:', `gyms/${gymId}/rankings/current`);
    
    // Get the current ranking document
    const rankingDoc = await getDoc(rankingRef);
    console.log('Ranking document exists:', rankingDoc.exists());
    
    if (!rankingDoc.exists()) {
      console.log('Rankings document does not exist');
      return [];
    }
    
    const data = rankingDoc.data();
    console.log('Ranking document data:', data);
    return data.rankings || [];
  } catch (error) {
    console.error('Error fetching gym ranking:', error);
    return [];
  }
};

/**
 * Fetches climbing routes by IDs
 */
export const getClimbingRoutesByIds = async (gymId: string, routeIds: string[]): Promise<Map<string, ClimbingRoute>> => {
  try {
    const routesMap = new Map<string, ClimbingRoute>();
    
    if (routeIds.length === 0) {
      console.log('No route IDs provided for lookup');
      return routesMap;
    }
    
    console.log(`Looking up ${routeIds.length} routes in gym ${gymId}`);
    
    // Get the gym document which contains all routes
    const gymRef = doc(db, 'gyms', gymId);
    const gymSnap = await getDoc(gymRef);
    
    if (!gymSnap.exists()) {
      console.error(`Gym ${gymId} not found`);
      return routesMap;
    }
    
    const gymData = gymSnap.data();
    const climbingRoutes = gymData.climbing_routes || {};
    console.log(`Gym has ${Object.keys(climbingRoutes).length} routes`);
    
    // Find each requested route in the gym's climbing_routes map
    for (const routeId of routeIds) {
      if (!routesMap.has(routeId)) {
        console.log(`Looking up route: ${routeId}`);
        
        if (climbingRoutes[routeId]) {
          console.log(`Found route ${routeId} in gym data`);
          const routeData = climbingRoutes[routeId];
          routesMap.set(routeId, new ClimbingRoute(routeId, routeData));
        } else {
          console.log(`Route ${routeId} not found in gym data`);
          // Create a dummy route with default points of 100 bananas for the new distribution system
          routesMap.set(routeId, new ClimbingRoute(routeId, { points: 100 }));
        }
      }
    }
    
    console.log(`Retrieved ${routesMap.size} routes`);
    return routesMap;
  } catch (error) {
    console.error('Error fetching routes by IDs:', error);
    return new Map();
  }
};

/**
 * Calculate rankings based on the gym ranking data with new banana distribution formula
 */
export const calculateRankings = async (gymId: string): Promise<any[]> => {
  try {
    // Fetch ranking data
    console.log('Calculating rankings for gym:', gymId);
    const rankingEntries = await getGymRanking(gymId);
    console.log('Ranking entries fetched:', rankingEntries);
    
    if (rankingEntries.length === 0) {
      console.log('No ranking entries found');
      return [];
    }
    
    // Get all unique route IDs
    const routeIdsSet = new Set<string>();
    rankingEntries.forEach(entry => {
      routeIdsSet.add(entry.routeId);
    });
    const routeIds = Array.from(routeIdsSet);
    
    // Add debugging for route retrieval
    console.log('Unique route IDs:', routeIds);
    const routesMap = await getClimbingRoutesByIds(gymId, routeIds);
    console.log('Routes map size:', routesMap.size);
    
    // Count climbers per route for the new banana distribution formula
    const climbersPerRoute = new Map<string, number>();
    rankingEntries.forEach(entry => {
      const currentCount = climbersPerRoute.get(entry.routeId) || 0;
      climbersPerRoute.set(entry.routeId, currentCount + 1);
    });
    
    console.log('Climbers per route:', Array.from(climbersPerRoute.entries()).map(([routeId, count]) => `${routeId}: ${count} climbers`));
    
    // Calculate points per climber
    const climberPoints = new Map<string, { id: string, name: string, points: number, routes: number }>(); 
    
    // First, collect all unique climber IDs to fetch their data
    const climberIds = new Set<string>();
    rankingEntries.forEach(entry => {
      climberIds.add(entry.climberId);
    });
    
    // Fetch all climber data to get usernames
    const climbersMap = new Map<string, string>(); // climberId -> username
    for (const climberId of Array.from(climberIds)) {
      try {
        const climberData = await getClimber(climberId);
        if (climberData) {
          // Use username if available, fallback to name, then to 'Unknown User'
          const displayName = climberData.username || climberData.name || 'Unknown User';
          climbersMap.set(climberId, displayName);
          console.log(`Fetched climber ${climberId}: username="${climberData.username}", name="${climberData.name}", using="${displayName}"`);
        } else {
          climbersMap.set(climberId, 'Unknown User');
          console.log(`Could not fetch climber data for ${climberId}, using "Unknown User"`);
        }
      } catch (error) {
        console.error(`Error fetching climber ${climberId}:`, error);
        climbersMap.set(climberId, 'Unknown User');
      }
    }
    
    for (const entry of rankingEntries) {
      const { climberId, routeId } = entry;
      const displayName = climbersMap.get(climberId) || 'Unknown User';
      console.log(`Processing entry for climber ${displayName} (${climberId}), route ${routeId}`);
      
      // Get route for points
      const route = routesMap.get(routeId);
      if (!route) {
        console.log(`Route ${routeId} not found in routes map, skipping`);
        continue;
      }
      
      // NEW BANANA DISTRIBUTION FORMULA:
      // Each route starts with 100 bananas (or the route's custom points if set)
      // Bananas are distributed equally among all climbers who completed the route
      const baseBananas = route.points || 100; // Default to 100 bananas per route
      const climbersOnThisRoute = climbersPerRoute.get(routeId) || 1;
      const bananasPerClimber = Math.round((baseBananas / climbersOnThisRoute) * 100) / 100; // Round to 2 decimal places
      
      console.log(`Route ${routeId}: ${baseBananas} total bananas ÷ ${climbersOnThisRoute} climbers = ${bananasPerClimber} bananas per climber`);
      
      // Update climber's points
      if (!climberPoints.has(climberId)) {
        console.log(`Creating new climber entry for ${displayName}`);
        climberPoints.set(climberId, { 
          id: climberId, 
          name: displayName, 
          points: 0,
          routes: 0 
        });
      }
      
      const climber = climberPoints.get(climberId)!;
      climber.points += bananasPerClimber;
      climber.routes += 1;
      console.log(`${displayName} now has ${climber.points} bananas from ${climber.routes} routes`);
    }
    
    // Convert to array and sort by points
    const rankings = Array.from(climberPoints.values());
    console.log('Rankings before sorting:', rankings);
    
    if (rankings.length === 0) {
      console.log('No rankings to display - climber points map is empty');
      return [];
    }
    
    rankings.sort((a, b) => b.points - a.points);
    console.log('Rankings after sorting:', rankings);
    
    // Add rank number and round final points for display
    const finalRankings = rankings.map((climber, index) => ({
      ...climber,
      rank: index + 1,
      points: Math.round(climber.points * 100) / 100 // Round to 2 decimal places for display
    }));
    console.log('Final rankings with rank numbers:', finalRankings);
    
    return finalRankings;
  } catch (error) {
    console.error('Error calculating rankings:', error);
    return [];
  }
};

/**
 * Records a new climb for a user using the hybrid approach:
 * - Stores recent climbs (up to 1000) directly in the user document
 * - Archives older climbs to a subcollection
 */
export const recordClimb = async (
  userId: string,
  gymId: string, 
  route: ClimbingRoute,
  performance: ClimbPerformance,
  notes?: string,
  hangs?: number,
  attempt?: number,
  reachedTop?: boolean,
  climbDate?: number
): Promise<void> => {
  try {
    // Create the climb record with all route details copied
    const climbRecord: ClimbRecord = {
      routeId: route.id,
      gymId: gymId,
      date: climbDate || Date.now(), // Use provided date or current timestamp
      performance: performance,
      grade: route.grade,
      style: route.style,
      notes: notes,
      hangs: hangs,
      attempt: attempt || 1,
      reachedTop: reachedTop !== undefined ? reachedTop : 
        (performance === ClimbPerformance.TOPPED || 
         performance === ClimbPerformance.FLASH || 
         performance === ClimbPerformance.ON_SIGHT || 
         performance === ClimbPerformance.REDPOINT),
      
      // Copy all route details for historical preservation
      color: route.color,
      rope: route.rope,
      setterId: route.setterId,
      setterName: route.setterName,
      routeNote: route.note,
      routeComments: route.comments,
      routeDate: route.date,
      status: route.status,
      picture: route.picture,
      location: route.location,
      points: route.points
    };

    // Get current user data
    const climber = await getClimber(userId);
    if (!climber) {
      console.error('User not found');
      throw new Error('User not found');
    }

    // Get current climbs array (or empty array if none exists)
    const currentClimbs: ClimbRecord[] = climber.climbs || [];

    // Check if we need to archive any climbs
    if (currentClimbs.length >= MAX_RECENT_CLIMBS) {
      // Get the oldest climbs that need to be archived
      const climbsToArchive = currentClimbs.slice(MAX_RECENT_CLIMBS - 1);
      
      // Archive these climbs
      await archiveClimbs(userId, climbsToArchive);
      
      // Keep only the most recent climbs in the array (minus one to make room for the new climb)
      const keptClimbs = currentClimbs.slice(0, MAX_RECENT_CLIMBS - 1);
      
      // Add new climb at the beginning
      const updatedClimbs = [climbRecord, ...keptClimbs];
      
      // Update user document with the new climbs array
      // Use updateDoc directly instead of updateClimberFields
      const userRef = doc(db, 'climbers', userId);
      await updateDoc(userRef, {
        climbs: updatedClimbs
      });
    } else {
      // We're still under the limit, just add the new climb to the array
      const userRef = doc(db, 'climbers', userId);
      
      // Add the new climb at the beginning of the array
      await updateDoc(userRef, {
        climbs: [climbRecord, ...currentClimbs]
      });
    }

    // Update the gym ranking document
    // Only record climbs with performance ON_SIGHT, FLASH, or REDPOINT in ranking
    if (performance === ClimbPerformance.ON_SIGHT || 
        performance === ClimbPerformance.FLASH || 
        performance === ClimbPerformance.REDPOINT) {
      console.log(`Recording ${ClimbPerformance[performance]} climb in rankings`);
      console.log('Route info for ranking:', {
        gymId,
        routeId: route.id,
        climberName: climber.name || 'Unknown Climber',
        points: route.points
      });
      
      await updateGymRanking(
        gymId,
        userId,
        climber.name || 'Unknown Climber', // Use climber's name
        route.id,
        route.points
      );
    } else {
      console.log(`Skipping ranking update - performance ${ClimbPerformance[performance]} is not eligible for ranking`);
    }

  } catch (error) {
    console.error('Error recording climb:', error);
    throw error;
  }
};

/**
 * Archives climbs to a subcollection for long-term storage
 */
const archiveClimbs = async (userId: string, climbs: ClimbRecord[]): Promise<void> => {
  try {
    // Reference to the user's archived climbs subcollection
    const archiveCollectionRef = collection(db, 'climbers', userId, 'archived_climbs');
    
    // Add each climb as a separate document in the subcollection
    for (const climb of climbs) {
      await addDoc(archiveCollectionRef, {
        ...climb,
        archivedAt: Date.now()
      });
    }
    
    console.log(`Archived ${climbs.length} climbs for user ${userId}`);
  } catch (error) {
    console.error('Error archiving climbs:', error);
    throw error;
  }
};

/**
 * Get all climbs for a user, combining recent and archived climbs
 * Optionally limit the total number of climbs returned
 */
export const getAllClimbs = async (
  userId: string, 
  maxResults: number = 100
): Promise<ClimbRecord[]> => {
  try {
    // Get the user document
    const userRef = doc(db, 'climbers', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      throw new Error('User not found');
    }
    
    // Get recent climbs from the user document
    const userData = userDoc.data();
    const recentClimbs: ClimbRecord[] = userData.climbs || [];
    
    // If we have enough recent climbs to satisfy the request, just return those
    if (recentClimbs.length >= maxResults) {
      return recentClimbs.slice(0, maxResults);
    }
    
    // If we need more, get archived climbs
    const needFromArchive = maxResults - recentClimbs.length;
    
    if (needFromArchive > 0) {
      // Query the archived climbs, most recent first
      const archiveCollectionRef = collection(db, 'climbers', userId, 'archived_climbs');
      const archiveQuery = query(
        archiveCollectionRef,
        orderBy('date', 'desc'),
        limit(needFromArchive)
      );
      
      const archiveSnapshot = await getDocs(archiveQuery);
      const archivedClimbs: ClimbRecord[] = [];
      
      archiveSnapshot.forEach(doc => {
        const climbData = doc.data() as ClimbRecord;
        archivedClimbs.push(climbData);
      });
      
      // Combine recent and archived climbs
      return [...recentClimbs, ...archivedClimbs];
    }
    
    // If we didn't need to get archived climbs
    return recentClimbs;
  } catch (error) {
    console.error('Error getting climbs:', error);
    throw error;
  }
};

/**
 * Get filtered climbs (e.g., for a specific gym)
 */
export const getFilteredClimbs = async (
  userId: string, 
  filter: Partial<ClimbRecord>,
  maxResults: number = 100
): Promise<ClimbRecord[]> => {
  try {
    // Get all climbs first (this could be optimized in a real implementation)
    const allClimbs = await getAllClimbs(userId, 1000); // Get a larger batch to filter from
    
    // Apply filters
    const filteredClimbs = allClimbs.filter(climb => {
      // Check each filter property
      for (const [key, value] of Object.entries(filter)) {
        if (climb[key as keyof ClimbRecord] !== value) {
          return false;
        }
      }
      return true;
    });
    
    // Return the requested number of results
    return filteredClimbs.slice(0, maxResults);
  } catch (error) {
    console.error('Error getting filtered climbs:', error);
    throw error;
  }
};

/**
 * Delete a climb from a user's climbs array by index and remove from gym ranking if necessary
 */
export const deleteClimb = async (userId: string, climbIndex: number): Promise<void> => {
  try {
    // Get the current user data
    const climber = await getClimber(userId);
    if (!climber || !climber.climbs) {
      throw new Error('User or climbs not found');
    }

    // Verify the climb index is valid
    if (climbIndex < 0 || climbIndex >= climber.climbs.length) {
      throw new Error('Invalid climb index');
    }

    // Get the climb info before deleting it
    const climbToDelete = climber.climbs[climbIndex];
    
    // Create a new array without the climb to delete
    const updatedClimbs = [...climber.climbs];
    updatedClimbs.splice(climbIndex, 1);

    // Update the user document with the new climbs array
    const userRef = doc(db, 'climbers', userId);
    await updateDoc(userRef, {
      climbs: updatedClimbs
    });

    // Remove from gym ranking if it was a ranking-worthy climb
    const isRankingPerformance = (perf: ClimbPerformance) => 
      perf === ClimbPerformance.ON_SIGHT || 
      perf === ClimbPerformance.FLASH || 
      perf === ClimbPerformance.REDPOINT;

    if (isRankingPerformance(climbToDelete.performance) && climbToDelete.gymId && climbToDelete.routeId) {
      try {
        console.log('Checking if ranking should be updated after climb deletion:', {
          gymId: climbToDelete.gymId,
          routeId: climbToDelete.routeId,
          climberId: userId,
          deletedPerformance: climbToDelete.performance
        });
        
        // Check if user has other qualifying climbs for the same route after deletion
        const remainingQualifyingClimbs = updatedClimbs.filter(climb => 
          climb.routeId === climbToDelete.routeId && 
          climb.gymId === climbToDelete.gymId &&
          isRankingPerformance(climb.performance)
        );
        
        console.log(`User has ${remainingQualifyingClimbs.length} remaining qualifying climbs for route ${climbToDelete.routeId}`);
        
        // Only remove from rankings if no other qualifying climbs exist for this route
        if (remainingQualifyingClimbs.length === 0) {
          console.log('No remaining qualifying climbs, removing from rankings');
          
          // Get the ranking document
          const rankingRef = doc(db, 'gyms', climbToDelete.gymId, 'rankings', 'current');
          const rankingDoc = await getDoc(rankingRef);
          
          if (rankingDoc.exists()) {
            // Get current rankings
            const rankings = rankingDoc.data().rankings || [];
            
            // Find and remove all entries for this user + route combination
            const updatedRankings = rankings.filter((entry: { climberId: string; routeId: string; timestamp: number }) => 
              !(entry.climberId === userId && entry.routeId === climbToDelete.routeId)
            );
            
            // Only update if we actually removed something
            if (updatedRankings.length !== rankings.length) {
              console.log(`Removed ${rankings.length - updatedRankings.length} entries from gym ranking`);
              
              // Update the ranking document
              await setDoc(rankingRef, { rankings: updatedRankings }, { merge: true });
            } else {
              console.log('No matching entries found in rankings');
            }
          } else {
            console.log('No ranking document found for gym:', climbToDelete.gymId);
          }
        } else {
          console.log('User still has other qualifying climbs for this route, keeping ranking entry');
        }
      } catch (rankingError) {
        console.error('Error updating gym ranking during climb deletion:', rankingError);
        // Don't throw error here to avoid failing the main deletion operation
      }
    }

    console.log(`Deleted climb at index ${climbIndex} for user ${userId}`);
  } catch (error) {
    console.error('Error deleting climb:', error);
    throw error;
  }
};

/**
 * Update a climb in a user's climbs array by index
 */
export const updateClimb = async (
  userId: string, 
  climbIndex: number, 
  updatedClimbData: Partial<ClimbRecord>
): Promise<void> => {
  try {
    // Get the current user data
    const climber = await getClimber(userId);
    if (!climber || !climber.climbs) {
      throw new Error('User or climbs not found');
    }

    // Verify the climb index is valid
    if (climbIndex < 0 || climbIndex >= climber.climbs.length) {
      throw new Error('Invalid climb index');
    }

    // Get the current climb and update it with new data
    const currentClimb = climber.climbs[climbIndex];
    const updatedClimb: ClimbRecord = {
      ...currentClimb,
      ...updatedClimbData,
      // Ensure we don't lose critical data
      routeId: currentClimb.routeId,
      gymId: currentClimb.gymId,
    };

    // Remove any fields that were explicitly set to null (for clearing optional fields)
    Object.keys(updatedClimb).forEach(key => {
      if ((updatedClimb as any)[key] === null) {
        delete (updatedClimb as any)[key];
      }
    });

    // Create a new array with the updated climb
    const updatedClimbs = [...climber.climbs];
    updatedClimbs[climbIndex] = updatedClimb;

    // Update the user document with the new climbs array
    const userRef = doc(db, 'climbers', userId);
    await updateDoc(userRef, {
      climbs: updatedClimbs
    });

    // Handle ranking updates if performance changed and affects rankings
    const oldPerformance = currentClimb.performance;
    const newPerformance = updatedClimb.performance;
    
    const isRankingPerformance = (perf: ClimbPerformance) => 
      perf === ClimbPerformance.ON_SIGHT || 
      perf === ClimbPerformance.FLASH || 
      perf === ClimbPerformance.REDPOINT;

    const wasInRanking = isRankingPerformance(oldPerformance);
    const shouldBeInRanking = isRankingPerformance(newPerformance);

    if (wasInRanking !== shouldBeInRanking) {
      // Performance changed between ranking and non-ranking, update accordingly
      try {
        const rankingRef = doc(db, 'gyms', currentClimb.gymId, 'rankings', 'current');
        const rankingDoc = await getDoc(rankingRef);
        
        if (rankingDoc.exists()) {
          const rankings = rankingDoc.data().rankings || [];
          
          if (wasInRanking && !shouldBeInRanking) {
            // Remove from ranking
            const updatedRankings = rankings.filter((entry: { climberId: string; routeId: string; timestamp: number }) => 
              !(entry.climberId === userId && entry.routeId === currentClimb.routeId)
            );
            await setDoc(rankingRef, { rankings: updatedRankings }, { merge: true });
            console.log('Removed climb from ranking due to performance change');
          } else if (!wasInRanking && shouldBeInRanking) {
            // Add to ranking
            const climberData = await getClimber(userId);
            if (climberData) {
              await updateGymRanking(
                currentClimb.gymId,
                userId,
                climberData.name || 'Unknown Climber',
                currentClimb.routeId,
                currentClimb.points
              );
              console.log('Added climb to ranking due to performance change');
            }
          }
        }
      } catch (rankingError) {
        console.error('Error updating gym ranking during climb update:', rankingError);
        // Don't throw error here to avoid failing the main update operation
      }
    }

    console.log(`Updated climb at index ${climbIndex} for user ${userId}`);
  } catch (error) {
    console.error('Error updating climb:', error);
    throw error;
  }
};
